import React, { FC, useRef, useState } from 'react';
import { FileUploadProps } from './fileUpload.type';
import closeIcon from '../../assets/closeIcon.svg';
import uploadIcon from '../../assets/upload.svg';
import defaultImage from '../../assets/defaultImage.svg';

const FileUpload: FC<FileUploadProps> = ({
  previewImage,
  onFileSelect,
  onDeselect,
  acceptedTypes = 'image/jpeg, image/png',
  maxSize = 2, // Default max size is 2MB
  previewUrl,
  selectedFileName,
  buttonLabel,
  label,
  supportedFormatsText,
  tabView,
  showEditProfile,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file type
    if (
      !file.type.match(acceptedTypes.replace(/\s/g, '').split(',').join('|'))
    ) {
      setError('File type not supported');
      return;
    }

    // Check file size (convert maxSize from MB to bytes)
    if (file.size > maxSize * 1024 * 1024) {
      setError(`File size exceeds ${maxSize}MB limit`);
      return;
    }

    setError(null);
    onFileSelect?.(file);
  };

  const handleDeselect = () => {
    if (onDeselect) {
      onDeselect();
    }
    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="flex flex-col items-center w-full">
      <div className="flex items-center gap-6 w-full">
        {/* Profile picture preview/placeholder */}
        {previewImage && (
          <div className="relative w-32 h-32 rounded-full overflow-hidden mb-4">
            {previewUrl ? (
              <>
                <img
                  src={previewUrl}
                  alt="Profile"
                  className="w-full h-full object-cover"
                  onClick={handleClick}
                />
                {showEditProfile && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeselect();
                    }}
                    className="absolute top-5 right-3 z-50 bg-black rounded-full p-1 shadow-md"
                    title="Remove file"
                  >
                    <img
                      src={closeIcon}
                      alt="Remove file"
                      className="w-4 h-4"
                    />
                  </button>
                )}
              </>
            ) : (
              <div
                className="flex items-center justify-center w-full h-full bg-gray-100"
                onClick={handleClick}
              >
                <img
                  src={defaultImage}
                  alt="Remove file"
                  height={120}
                  width={120}
                  className="w-full h-full object-contain"
                />
              </div>
            )}
          </div>
        )}
        <div className="flex-1">
          {/* Label */}
          {label && <div className="mb-2">{label}</div>}

          <div
            className={`flex ${
              tabView ? 'justify-center' : 'justify-between'
            } items-center w-full gap-4 mb-2`}
          >
            {/* Upload button - always visible */}
            <button
              type="button"
              onClick={handleClick}
              className={`flex items-center px-4 py-2 bg-white ${
                !tabView ? 'border border-[#D8D8D8] font-medium' : ''
              } gap-3 rounded-[30px] text-sm`}
            >
              <img src={uploadIcon} alt="Remove file" height={16} width={16} />
              {buttonLabel && buttonLabel}
            </button>

            {/* Show file name and deselect option if file is selected */}
            {!tabView && selectedFileName && previewUrl && (
              <div className="flex-1">
                <div className="flex items-center ">
                  <span className="text-sm font-medium text-gray-700">
                    {selectedFileName}
                  </span>
                  <button
                    type="button"
                    onClick={handleDeselect}
                    className="ml-2 p-1 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
                    title="Remove file"
                  >
                    <img
                      src={closeIcon}
                      alt="Remove file"
                      height={30}
                      width={30}
                    />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Supported formats text */}
          {supportedFormatsText && (
            <p className="text-xs text-gray-500">{supportedFormatsText}</p>
          )}
        </div>

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept={acceptedTypes}
          className="hidden"
        />
      </div>

      {/* Error message */}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};

export default FileUpload;
